import { NetworkStatus } from '@apollo/client';
import type { TextFieldProps } from '@mui/material/TextField';
import TextField from '@mui/material/TextField';

import Autocomplete from '@mui/material/Autocomplete';

import CircularProgress from '@mui/material/CircularProgress';

import Paper from '@mui/material/Paper';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import classNames from 'classnames';

import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { tss } from 'tss-react/mui';

import { graphql, type DocumentType } from '@/gql';

import { MultiGroupFilterGroupOption } from './MultiGroupFilterGroupOption';
import {
  MultiGroupFilterListbox,
  type MultiGroupFilterListboxConfiguration,
} from './MultiGroupFilterListbox';
import { MultiGroupFilterGroupTags } from './MultiGroupFilterGroupTags';
import { MultiGroupFilterSelectAll } from './MultiGroupFilterSelectAll';

const useStyles = tss.create(({ theme }) => {
  return {
    chipInputs: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    root: {
      paddingBottom: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    listActions: {
      padding: theme.spacing(1),
      display: 'flex',
      justifyContent: 'flex-end',
    },
  };
});

export const SERVICE_GROUPS_PAGE_SIZE = 20;

export const MULTI_GROUP_FILTER_FRAGMENT = graphql(`
  fragment multiGroupFilterGroupFields on Group {
    _id
    name
    iconUrl
  }
`);

export type FilterGroup = DocumentType<typeof MULTI_GROUP_FILTER_FRAGMENT> & {
  // Groups with a message are still listed, but can't be selected. The
  // message is shown next to the group name.
  disabledMessage?: string | null;
};

export interface MultiGroupFilterSelectAll {
  loading: boolean;
  onSelectAll: () => void;
}

export interface MultiGroupFilterServerSideSearch {
  inputValue: string;
  onInputChange: (event: unknown, value: string) => void;
  networkStatus: NetworkStatus;
  hasNextPage: boolean;
  pageSize: number;
  remainingOptionsCount: number;
  fetchMore: () => void;
  // Only the loaded pages are in the options, so selecting every group that
  // matches the search has to be handled by the component owning the query.
  selectAll: MultiGroupFilterSelectAll;
}

export interface MultiGroupFilterProps {
  className?: string;
  groupOptions: FilterGroup[];
  selectedOptions: FilterGroup[];
  loading: boolean;
  label?: string;
  placeholder?: string;
  onChange: (selectedOptions: FilterGroup[]) => void;
  tagLimit?: number | undefined;
  textFieldProps?: TextFieldProps;
  variant?: 'filled' | 'outlined';
  onInputChange?: (event: unknown, value: string) => void;
  inputValue?: string;
  serverSideSearch?: MultiGroupFilterServerSideSearch;
}

export const MultiGroupFilter = ({
  className,
  groupOptions,
  selectedOptions,
  loading,
  label = 'Selected groups',
  placeholder = 'Click here to add more',
  onChange,
  tagLimit = 5,
  textFieldProps = {},
  variant = 'filled',
  onInputChange,
  inputValue,
  serverSideSearch,
}: MultiGroupFilterProps) => {
  const { classes } = useStyles();
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (selectedOptions.length < tagLimit) {
      setShowMore(false);
    }
  }, [selectedOptions.length, tagLimit]);

  const fetchingMoreOptions =
    serverSideSearch?.networkStatus === NetworkStatus.fetchMore;

  // MUI remounts the listbox whenever it is given a new component, which
  // would reset its scroll position on every render. The component is
  // memoised so it stays stable, and reads the latest props through this ref.
  const listboxConfiguration = useRef<MultiGroupFilterListboxConfiguration>({
    groupOptions,
    selectedOptions,
    onSelectedOptionsChange: onChange,
    serverSideSearch,
  });

  listboxConfiguration.current = {
    groupOptions,
    selectedOptions,
    onSelectedOptionsChange: onChange,
    serverSideSearch,
  };

  // Rendered in the paper rather than the listbox, so it holds its place while
  // the options scroll and is still there when they run out. The paper is
  // rendered whenever the popup is open, options or no options.
  const PaperComponent = useMemo(
    () =>
      forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
        function MultiGroupFilterPaperComponent({ children, ...props }, ref) {
          return (
            <Paper ref={ref} {...props}>
              <MultiGroupFilterSelectAll
                {...listboxConfiguration.current}
                className={classes.listActions}
              />
              {children}
            </Paper>
          );
        },
      ),
    [classes.listActions],
  );

  const ListboxComponent = useMemo(
    () =>
      forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
        function MultiGroupFilterListboxComponent({ children, ...props }, ref) {
          return (
            <MultiGroupFilterListbox
              ref={ref}
              {...props}
              groupOptions={listboxConfiguration.current.groupOptions}
              serverSideSearch={listboxConfiguration.current.serverSideSearch}
            >
              {children}
            </MultiGroupFilterListbox>
          );
        },
      ),
    // The listbox reads the configuration through a ref, so it never needs
    // rebuilding; remounting it would lose the list's scroll position.
    [],
  );

  return (
    <div className={classNames(classes.root, className)}>
      <Autocomplete
        data-tour-id="my-hub-group-selector"
        multiple
        id="multi-group-filter"
        options={groupOptions}
        filterOptions={serverSideSearch ? (options) => options : undefined}
        filterSelectedOptions
        disableCloseOnSelect
        value={selectedOptions}
        loading={loading}
        getOptionLabel={(option) => option?.name ?? ''}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        getOptionDisabled={(option) => Boolean(option.disabledMessage)}
        renderOption={(props, option, { selected }) => (
          <MultiGroupFilterGroupOption
            key={option._id ?? ''}
            option={option}
            selected={selected}
            disabled={Boolean(option.disabledMessage)}
            disabledMessage={option.disabledMessage ?? undefined}
            props={props}
          />
        )}
        fullWidth
        renderTags={(value: FilterGroup[], getTagProps) => {
          return (
            <MultiGroupFilterGroupTags
              value={value}
              getTagProps={getTagProps}
              showMore={showMore}
              setShowMore={setShowMore}
              classes={{ chipInputs: classes.chipInputs }}
            />
          );
        }}
        popupIcon={
          loading || fetchingMoreOptions ? (
            <CircularProgress disableShrink size={16} />
          ) : (
            <ArrowDropDownIcon />
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant={variant}
            label={label}
            placeholder={placeholder}
            {...textFieldProps}
          />
        )}
        onChange={(event, newValue) => {
          onChange(newValue);
        }}
        onInputChange={(event, value, reason) => {
          if (!serverSideSearch) {
            onInputChange?.(event, value);
            return;
          }

          // MUI resets the input after a selection and, because clearOnBlur
          // defaults on, empties it again on blur. Either would drop the
          // server search term and pull back the unfiltered first page, so
          // only the user's own typing and the clear button change it.
          if (reason !== 'input' && reason !== 'clear') {
            return;
          }

          serverSideSearch.onInputChange(event, value);
        }}
        inputValue={serverSideSearch?.inputValue ?? inputValue}
        slotProps={{
          paper: {
            component: PaperComponent,
          },
          listbox: {
            component: ListboxComponent,
            sx: serverSideSearch
              ? { padding: 0, maxHeight: 'none', overflow: 'hidden' }
              : { padding: 0 },
          },
        }}
      />
    </div>
  );
};
